import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { getWaterPercentage, getWaterOptions } from "@/lib/waterUtils";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ErrorBoundary from "@/components/ErrorBoundary";
import ChunkErrorBoundary from "@/components/ChunkErrorBoundary";
import { BrowserRouter, Routes, Route, Link, useNavigate, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Menu, User, HelpCircle, ClipboardCheck, Sparkles, Clock, ChevronRight, CheckCircle, Circle, Sun, Moon, Droplets, Plus as PlusIcon, Minus, Heart } from "lucide-react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { initVersionCheck, startVersionMonitor } from "@/lib/versionCheck";

import { differenceInDays } from "date-fns";
const Home = lazyWithRetry(() => import("./pages/Home"));
const Skin = lazyWithRetry(() => import("./pages/Skin"));
const FaceScore = lazyWithRetry(() => import("./pages/FaceScore"));
const FaceScoreBreakdown = lazyWithRetry(() => import("./pages/FaceScoreBreakdown"));
const Signup = lazyWithRetry(() => import("./pages/Signup"));
const ForgotPassword = lazyWithRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const Recommendations = lazyWithRetry(() => import("./pages/Recommendations"));
const Referrals = lazyWithRetry(() => import("./pages/Referrals"));
const Community = lazyWithRetry(() => import("./pages/Community"));
const Challenges = lazyWithRetry(() => import("./pages/Challenges"));
const ChallengeDetail = lazyWithRetry(() => import("./pages/ChallengeDetail"));
const Me = lazyWithRetry(() => import("./pages/Me"));
import Login from "./pages/Login";
const FirstScan = lazyWithRetry(() => import("./pages/FirstScan"));
const FirstScanGuide = lazyWithRetry(() => import("./pages/FirstScanGuide"));
const Rescan = lazyWithRetry(() => import("./pages/Rescan"));
const FirstResult = lazyWithRetry(() => import("./pages/FirstResult"));
const CompleteResult = lazyWithRetry(() => import("./pages/CompleteResult"));
const FirstSkinSync = lazyWithRetry(() => import("./pages/FirstSkinSync"));
const SkinSync = lazyWithRetry(() => import("./pages/SkinSync"));
const SkinSyncComplete = lazyWithRetry(() => import("./pages/SkinSyncComplete"));
const PulseCheck = lazyWithRetry(() => import("./pages/PulseCheck"));
const PulseCheckComplete = lazyWithRetry(() => import("./pages/PulseCheckComplete"));
const AddProduct = lazyWithRetry(() => import("./pages/AddProduct"));
const Paywall = lazyWithRetry(() => import("./pages/Paywall"));
const PaymentCheckout = lazyWithRetry(() => import("./pages/PaymentCheckout"));
const PaymentSuccess = lazyWithRetry(() => import("./pages/PaymentSuccess"));
const ProfileSettings = lazyWithRetry(() => import("./pages/ProfileSettings"));
const DataPrivacy = lazyWithRetry(() => import("./pages/DataPrivacy"));
const DeleteAccountRequest = lazyWithRetry(() => import("./pages/DeleteAccountRequest"));
const Notifications = lazyWithRetry(() => import("./pages/Notifications"));
const SubscriptionBilling = lazyWithRetry(() => import("./pages/SubscriptionBilling"));
const Giving = lazyWithRetry(() => import("./pages/Giving"));
const FAQ = lazyWithRetry(() => import("./pages/FAQ"));
const SupportCenter = lazyWithRetry(() => import("./pages/SupportCenter"));
const SuggestionsFeedback = lazyWithRetry(() => import("./pages/SuggestionsFeedback"));
const Progress = lazyWithRetry(() => import("./pages/Progress"));
const ModeratorConsole = lazyWithRetry(() => import("./pages/ModeratorConsole"));
const AdminConsole = lazyWithRetry(() => import("./pages/AdminConsole"));
const AdminIntelligence = lazyWithRetry(() => import("./pages/Admin"));
const ExportData = lazyWithRetry(() => import("./pages/ExportData"));
const SecurityAuditLog = lazyWithRetry(() => import("./pages/SecurityAuditLog"));
const Beta = lazyWithRetry(() => import("./pages/Beta"));
// Onboarding is NOT lazy-loaded - it's a critical path for new users
import Onboarding from "./pages/Onboarding";
const LearningBlog = lazyWithRetry(() => import("./pages/LearningBlog"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Waitlist = lazyWithRetry(() => import("./pages/Waitlist"));
const RoutinePage = lazyWithRetry(() => import("./pages/Routine"));
const RoutineGuide = lazyWithRetry(() => import("./pages/RoutineGuide"));
const RoutineIQResults = lazyWithRetry(() => import("./pages/RoutineIQResults"));
const FillRoutineSlot = lazyWithRetry(() => import("./pages/FillRoutineSlot"));

const Goals = lazyWithRetry(() => import("./pages/Goals"));
const GoalSettings = lazyWithRetry(() => import("./pages/GoalSettings"));
const ClearRoutineData = lazyWithRetry(() => import("./pages/ClearRoutineData"));
const ProductDetail = lazyWithRetry(() => import("./pages/ProductDetail"));
const Product = lazyWithRetry(() => import("./pages/Product"));
const BrandPage = lazyWithRetry(() => import("./pages/BrandPage"));
const IngredientDetail = lazyWithRetry(() => import("./pages/IngredientDetail"));
const NaturalIngredientDetail = lazyWithRetry(() => import("./pages/NaturalIngredientDetail"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const ImportCeraVe = lazyWithRetry(() => import("./pages/ImportCeraVe"));
const TestScan = lazyWithRetry(() => import("./pages/TestScan"));
const AdminTestScan = lazyWithRetry(() => import("./pages/AdminTestScan"));
const PerformanceTestPage = lazyWithRetry(() => import("./pages/PerformanceTestPage"));
const DomainSolutions = lazyWithRetry(() => import("./pages/DomainSolutions"));
const AdminTemplateGenerator = lazyWithRetry(() => import("./pages/AdminTemplateGenerator"));
const AdminBarcodes = lazyWithRetry(() => import("./pages/AdminBarcodes"));
const AdminProductImport = lazyWithRetry(() => import("./pages/AdminProductImport"));
const AdminProductReports = lazyWithRetry(() => import("./pages/AdminProductReports"));
const ImportIngredients = lazyWithRetry(() => import("./pages/admin/ImportIngredients"));
import ProtectedRoute from "./components/ProtectedRoute";
import AuthOnlyRoute from "./components/AuthOnlyRoute";
import RoleProtectedRoute from "./components/RoleProtectedRoute";
import RootRedirect from "./components/RootRedirect";
import BottomNavigation from "@/components/BottomNavigation";
import Header from "./components/Header";
import CookieBanner from "./components/CookieBanner";
import { PushNotificationSetup } from "./components/PushNotificationSetup";
import { StagingGuard } from "./components/StagingGuard";
import { StagingBanner } from "./components/StagingBanner";
import { BetaBanner } from "./components/BetaBanner";

// Lazy load legal/policy pages too
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazyWithRetry(() => import("./pages/TermsConditions"));
const WellnessDisclaimer = lazyWithRetry(() => import("./pages/WellnessDisclaimer"));
const CookiePolicy = lazyWithRetry(() => import("./pages/CookiePolicy"));
import { useMixpanelPageTracking } from "./hooks/useMixpanelTracking";
import RateAppDialog from "./components/RateAppDialog";
import { useAppRating } from "./hooks/useAppRating";
import SecurityHeaders from "./components/SecurityHeaders";
import { HelmetProvider } from 'react-helmet-async';
import { useErrorLogger } from "./hooks/useErrorLogger";
import { useIngredientEnrichment } from "./hooks/useIngredientEnrichment";

function AppContent() {
  useMixpanelPageTracking();
  useErrorLogger(); // Track errors and navigation
  useIngredientEnrichment(); // One-time ingredient data enrichment
  const {
    showRateDialog,
    closeRateDialog
  } = useAppRating();
  
  // Initialize version check on app load
  useEffect(() => {
    initVersionCheck();
    const cleanup = startVersionMonitor();
    return cleanup;
  }, []);
  
  return <RateAppDialog open={showRateDialog} onClose={closeRateDialog} />;
}
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      // 5 minutes - optimal balance
      gcTime: 15 * 60 * 1000,
      // 15 minutes - keep data cached
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      // Don't refetch on mount if data is fresh
      retry: 1,
      // Reduce retries from 2 to 1
      networkMode: 'online'
    },
    mutations: {
      retry: 1
    }
  }
});
const App = () => <ErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
        <BrowserRouter>
        <StagingGuard>
          <StagingBanner />
          <BetaBanner />
          <AppContent />
          <ChunkErrorBoundary>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="text-center"><div className="mx-auto h-12 w-12 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent"></div><p className="mt-4 text-muted-foreground">Loading…</p></div></div>}>
            <SecurityHeaders />
            <Routes>
              <Route path="/" element={<RootRedirect />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/onboarding" element={<Onboarding />} />

              {/* Protected Routes */}
              <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
              <Route path="/skin" element={<ProtectedRoute><Skin /></ProtectedRoute>} />
              <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
              <Route path="/routine" element={<ProtectedRoute><RoutinePage /></ProtectedRoute>} />
              <Route path="/routine/fill-slot" element={<ProtectedRoute><FillRoutineSlot /></ProtectedRoute>} />
              <Route path="/routine-iq" element={<ProtectedRoute><RoutineGuide /></ProtectedRoute>} />
              <Route path="/routine-iq-results" element={<ProtectedRoute><RoutineIQResults /></ProtectedRoute>} />
              <Route path="/me" element={<ProtectedRoute><Me /></ProtectedRoute>} />
              <Route path="/beta-hub" element={<ProtectedRoute><Beta /></ProtectedRoute>} />
              <Route path="/product" element={<ProtectedRoute><Product /></ProtectedRoute>} />
              <Route path="/product/:productId" element={<ProtectedRoute><ProductDetail /></ProtectedRoute>} />
              <Route path="/brand/:brandId" element={<ProtectedRoute><BrandPage /></ProtectedRoute>} />
              <Route path="/ingredient/:ingredientId" element={<ProtectedRoute><IngredientDetail /></ProtectedRoute>} />
              <Route path="/natural-ingredient/:ingredientId" element={<ProtectedRoute><NaturalIngredientDetail /></ProtectedRoute>} />
              <Route path="/progress" element={<ProtectedRoute><Progress /></ProtectedRoute>} />
              <Route path="/learning-blog" element={<ProtectedRoute><LearningBlog /></ProtectedRoute>} />
              <Route path="/learning-blog/:slug" element={<ProtectedRoute><LearningBlog /></ProtectedRoute>} />
              <Route path="/blog" element={<ProtectedRoute><Blog /></ProtectedRoute>} />
              <Route path="/blog/:slug" element={<ProtectedRoute><Blog /></ProtectedRoute>} />
              
              {/* FaceScore/Scan Routes */}
              <Route path="/face-score" element={<ProtectedRoute><FaceScore /></ProtectedRoute>} />
              <Route path="/myskin" element={<ProtectedRoute><FaceScoreBreakdown /></ProtectedRoute>} />
              <Route path="/first-scan" element={<ProtectedRoute><FirstScan /></ProtectedRoute>} />
              <Route path="/first-scan-guide" element={<ProtectedRoute><FirstScanGuide /></ProtectedRoute>} />
              <Route path="/rescan" element={<ProtectedRoute><Rescan /></ProtectedRoute>} />
              <Route path="/first-result" element={<ProtectedRoute><FirstResult /></ProtectedRoute>} />
              <Route path="/updated-result" element={<ProtectedRoute><CompleteResult /></ProtectedRoute>} />
              
              {/* SkinSync & PulseCheck Routes */}
              <Route path="/skin-sync" element={<ProtectedRoute><SkinSync /></ProtectedRoute>} />
              <Route path="/first-skin-sync" element={<ProtectedRoute><FirstSkinSync /></ProtectedRoute>} />
              <Route path="/skin-sync-complete" element={<ProtectedRoute><SkinSyncComplete /></ProtectedRoute>} />
              <Route path="/skin-sync/complete" element={<Navigate to="/skin-sync-complete" replace />} />
              <Route path="/pulse-check" element={<ProtectedRoute><PulseCheck /></ProtectedRoute>} />
              <Route path="/pulse-check-complete" element={<ProtectedRoute><PulseCheckComplete /></ProtectedRoute>} />
              <Route path="/pulse-check/complete" element={<Navigate to="/pulse-check-complete" replace />} />
              
              {/* Routine & Product Routes */}
              <Route path="/products" element={<Navigate to="/product" replace />} />
              <Route path="/add-product" element={<ProtectedRoute><AddProduct /></ProtectedRoute>} />
              <Route path="/goals" element={<ProtectedRoute><Goals /></ProtectedRoute>} />
              <Route path="/settings/goals" element={<ProtectedRoute><GoalSettings /></ProtectedRoute>} />
              <Route path="/clear-routine-data" element={<ProtectedRoute><ClearRoutineData /></ProtectedRoute>} />
              <Route path="/recommendations" element={<ProtectedRoute><Recommendations /></ProtectedRoute>} />
              <Route path="/daily-log" element={<ProtectedRoute><Home /></ProtectedRoute>} />
              
              {/* Challenge Routes */}
              <Route path="/challenges" element={<ProtectedRoute><Challenges /></ProtectedRoute>} />
              <Route path="/challenges/:challengeId" element={<ProtectedRoute><ChallengeDetail /></ProtectedRoute>} />
              
              {/* Profile & Settings Routes */}
              <Route path="/profile-settings" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
              <Route path="/data-privacy" element={<ProtectedRoute><DataPrivacy /></ProtectedRoute>} />
              <Route path="/delete-account-request" element={<ProtectedRoute><DeleteAccountRequest /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
              <Route path="/subscription-billing" element={<ProtectedRoute><SubscriptionBilling /></ProtectedRoute>} />
              <Route path="/export-data" element={<ProtectedRoute><ExportData /></ProtectedRoute>} />
              <Route path="/security-audit-log" element={<ProtectedRoute><SecurityAuditLog /></ProtectedRoute>} />
              
              {/* Other Protected Routes */}
              <Route path="/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
              <Route path="/giving" element={<ProtectedRoute><Giving /></ProtectedRoute>} />
              <Route path="/faq" element={<ProtectedRoute><FAQ /></ProtectedRoute>} />
              <Route path="/support-center" element={<ProtectedRoute><SupportCenter /></ProtectedRoute>} />
              <Route path="/suggestions-feedback" element={<ProtectedRoute><SuggestionsFeedback /></ProtectedRoute>} />
              
              {/* Domain Solutions */}
              <Route path="/domains/:domainName/solutions" element={<ProtectedRoute><DomainSolutions /></ProtectedRoute>} />
              
              {/* Payment Routes */}
              <Route path="/paywall" element={<ProtectedRoute><Paywall /></ProtectedRoute>} />
              <Route path="/payment-checkout" element={<ProtectedRoute><PaymentCheckout /></ProtectedRoute>} />
              <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
              
              {/* Legal Pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-conditions" element={<TermsConditions />} />
              <Route path="/wellness-disclaimer" element={<WellnessDisclaimer />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              
              {/* Admin & Moderator Routes */}
              <Route path="/moderator-console" element={<RoleProtectedRoute requiredRole="moderator"><ModeratorConsole /></RoleProtectedRoute>} />
              <Route path="/admin" element={<RoleProtectedRoute requiredRole="admin"><AdminConsole /></RoleProtectedRoute>} />
              <Route path="/admin-console" element={<RoleProtectedRoute requiredRole="admin"><AdminConsole /></RoleProtectedRoute>} />
              <Route path="/admin-intelligence" element={<RoleProtectedRoute requiredRole="admin"><AdminIntelligence /></RoleProtectedRoute>} />
              <Route path="/import-cerave" element={<RoleProtectedRoute requiredRole="admin"><ImportCeraVe /></RoleProtectedRoute>} />
              <Route path="/test-scan" element={<RoleProtectedRoute requiredRole="admin"><TestScan /></RoleProtectedRoute>} />
              <Route path="/admin-test-scan" element={<RoleProtectedRoute requiredRole="admin"><AdminTestScan /></RoleProtectedRoute>} />
              <Route path="/performance-test" element={<ProtectedRoute><PerformanceTestPage /></ProtectedRoute>} />
              <Route path="/admin-template-generator" element={<RoleProtectedRoute requiredRole="admin"><AdminTemplateGenerator /></RoleProtectedRoute>} />
              <Route path="/admin-barcodes" element={<RoleProtectedRoute requiredRole="admin"><AdminBarcodes /></RoleProtectedRoute>} />
              <Route path="/admin-product-import" element={<RoleProtectedRoute requiredRole="admin"><AdminProductImport /></RoleProtectedRoute>} />
              <Route path="/admin-product-reports" element={<RoleProtectedRoute requiredRole="admin"><AdminProductReports /></RoleProtectedRoute>} />
              <Route path="/admin-import-ingredients" element={<RoleProtectedRoute requiredRole="admin"><ImportIngredients /></RoleProtectedRoute>} />
              
              {/* Public Routes */}
              <Route path="/waitlist" element={<Waitlist />} />
              <Route path="/beta" element={<Waitlist />} />

              {/* Redirect common route mistakes */}
          <Route path="/facescore" element={<Navigate to="/myskin" replace />} />
          <Route path="/face-score" element={<Navigate to="/myskin" replace />} />
          <Route path="/face-score/breakdown" element={<Navigate to="/myskin" replace />} />
              <Route path="/face-scan" element={<Navigate to="/first-scan" replace />} />
              <Route path="/skinsync" element={<Navigate to="/skin-sync" replace />} />
              <Route path="/pulsecheck" element={<Navigate to="/pulse-check" replace />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </ChunkErrorBoundary>
        </StagingGuard>
        </BrowserRouter>
      <PushNotificationSetup />
      <CookieBanner />
      </TooltipProvider>
    </QueryClientProvider>
    </HelmetProvider>
  </ErrorBoundary>;
export default App;